import { cloneDeep } from 'lodash';
import {
  Dispatch,
  forwardRef,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { NotificationPosition } from 'core/enums';
import { useFormContext } from 'core/states';
import { IStepButtonOptions } from 'core/types';
import { IPart } from 'parts/types';
import { getLineCellValue } from 'quotes/components/QuotePartList/utils';
import { QuoteEntityType, QuotePartUnifiedStatus, QuoteType } from 'quotes/enums';
import { QuotePartPricingHelper } from 'quotes/helpers';
import { quotePartService } from 'quotes/services';
import { ICustomerQuote, ICustomerQuotePart, IQuotePart, IQuotePartLineForm } from 'quotes/types';
import { parseSpaSelectionAvailable } from 'quotes/utils';
import { ITaxonomyGroup } from 'taxonomy/types';

import { PricingStateProvider } from './CostSection/CostFormFields';
import { LineDetailsForm } from './LineDetailsForm';
import { UpsertLineFormProvider } from './state';
import { getLineDetailsDefaultValues } from './utils';

interface IProps {
  quoteId: number;
  quoteType?: QuoteType;
  quotePart: IQuotePart;
  isEdit: boolean;
  isOpen: boolean;
  onClose: () => void;
  isAlternateVersionEnabled?: boolean;
  entityType?: QuoteEntityType;
  setHasExternalTaxonomy?: Dispatch<SetStateAction<boolean>>;
  onSubmit?: () => void;
  quote: ICustomerQuote;
  setValidatePartTaxonomyPath?: Dispatch<SetStateAction<ITaxonomyGroup[]>>;
  defaultValues?: IQuotePartLineForm;
  saveAndAddNextOptions?: IStepButtonOptions;
  onValidatingChange?: (isValidating: boolean) => void;
  initialPart?: Partial<IPart>;
}

export const LineDetailsFormWithProvider = forwardRef(function LineDetailsFormWithProvider(
  {
    quoteId,
    quoteType = QuoteType.CUSTOMER,
    quotePart,
    isEdit,
    isOpen,
    onClose,
    isAlternateVersionEnabled = false,
    entityType,
    setHasExternalTaxonomy,
    onSubmit,
    quote,
    setValidatePartTaxonomyPath,
    defaultValues: initialDefaultValues,
    saveAndAddNextOptions,
    onValidatingChange,
    initialPart,
  }: IProps,
  ref
) {
  // The values the line opened with, kept per line: a failed save resets the form to the user's
  // own values, which must not become the baseline the unsaved-changes check compares against.
  const initialLineValues = useRef<{ lineId: number; values: Partial<IQuotePartLineForm> }>(null);

  const { getValues, reset, setValue, watch } = useFormContext();

  const defaultValues = useMemo(
    () =>
      getLineDetailsDefaultValues(
        quotePart,
        initialDefaultValues,
        quote?.pricingDate,
        quote?.pricingStrategy,
        isAlternateVersionEnabled
      ),
    [
      initialDefaultValues,
      isAlternateVersionEnabled,
      quote?.pricingDate,
      quote?.pricingStrategy,
      quotePart,
    ]
  );

  useEffect(() => {
    setTimeout(() => {
      if (!isOpen && !isEdit) {
        return;
      }

      reset(defaultValues);

      if (isEdit && initialLineValues.current?.lineId !== quotePart?.id) {
        // getValues() is a shallow copy: nested values would follow the form's later edits.
        initialLineValues.current = { lineId: quotePart?.id, values: cloneDeep(getValues()) };
      }
    });
  }, [defaultValues, getValues, isEdit, isOpen, quotePart?.id, reset]);

  const getInitialValues = useCallback(
    () =>
      initialLineValues.current?.lineId === quotePart?.id ? initialLineValues.current.values : null,
    [quotePart?.id]
  );

  const unifiedPartStatus = useMemo(() => {
    if (!quotePart?.id) {
      return null;
    }

    if ((quotePart as ICustomerQuotePart)?.ddpItemFlag) {
      return QuotePartUnifiedStatus.ACTIVE;
    }

    return QuotePartUnifiedStatus.INACTIVE;
  }, [quotePart]);

  const costData = useMemo(
    () => QuotePartPricingHelper.mapQuotePartToCostData(quotePart as ICustomerQuotePart).costData,
    [quotePart]
  );

  const priceData = useMemo(
    () => QuotePartPricingHelper.mapQuotePartToPriceData(quotePart as ICustomerQuotePart),
    [quotePart]
  );

  const partValue = watch('part');
  const manufacturerValue = watch('manufacturer');

  const getCalculatePricesParams = useCallback(
    () => ({
      quoteId,
      number: getValues('part')?.number || '',
      manufacturerId: getValues('manufacturer')?.id,
      vendorNumber: getValues('part')?.vendorNumber,
      accountsPayableId: getValues('accountsPayableId'),
      quantity: getValues('quantity'),
      uom: getValues('uom'),
      lineReference: getLineCellValue(quotePart),
    }),
    [getValues, quoteId, quotePart]
  );

  return (
    <PricingStateProvider
      isAlternateVersionEnabled={isAlternateVersionEnabled}
      priceDecimals={quotePart?.quotePriceDecimals}
      initialCostData={costData}
      initialPriceData={priceData}
      initialUnifiedPartStatus={unifiedPartStatus}
      initialHasQuantityBreaks={
        quotePart?.hasQuantityBreaks == null ? null : Number(quotePart.hasQuantityBreaks) === 1
      }
      initialSpaSelectionAvailable={parseSpaSelectionAvailable(
        quotePart?.externalSpaSelectionAvailable
      )}
      partNumber={partValue?.number}
      manufacturerId={manufacturerValue?.id}
      quoteId={quoteId}
      quotePart={quotePart as ICustomerQuotePart}
      enabledPricingMultiplier={isOpen}
      getCalculatePricesParams={getCalculatePricesParams}
      externalDisabled={false}
      notificationPosition={NotificationPosition.DRAWER}
    >
      <UpsertLineFormProvider
        {...{
          isOpen,
          entityId: quoteId,
          quoteType,
          entityLineId: quotePart?.id,
          partId: watch('id'),
          partValue: watch('part'),
          manufacturerValue: watch('manufacturer'),
          descriptionValue: watch('description'),
          quantityValue: watch('quantity'),
          purchaseCostValue: watch('purchaseCost'),
          uomValue: watch('uom'),
          reset,
          getValues,
          setValue,
          validatePartAction: quotePartService.validate,
          onClose,
          disabledPriceCall: QuoteType.CUSTOMER !== quoteType,
          setHasExternalTaxonomy,
          onSubmit,
          saveAndAddNextOptions,
          setValidatePartTaxonomyPath,
          onValidatingChange,
          initialPart,
        }}
      >
        <LineDetailsForm
          ref={ref}
          quoteId={quoteId}
          quoteType={quoteType}
          quotePart={quotePart}
          isEdit={isEdit}
          onClose={onClose}
          isAlternateVersionEnabled={isAlternateVersionEnabled}
          entityType={entityType}
          quote={quote}
          isPartLocked={!!initialPart}
          getInitialValues={getInitialValues}
        />
      </UpsertLineFormProvider>
    </PricingStateProvider>
  );
});
